/* Copyright (c) 2020-present CloudIO Inc */

import { atom, emptyFunction, useStateXAtomValue } from '@cloudio-saas/statex';
import { autoPageRefreshAtom } from 'designer/atoms';
import useSubscription from 'hooks/useSubscription';
import { usePlatform } from 'platform/PlatformHooks';
import React from 'react';
import useUser from 'root/useUser';
import { ulid } from 'ulid';

export interface PageUpdateEvent {
  pageId: string;
  appUid: string;
  id: string;
  userName: string;
  ts: string;
  displayName: string;
}

export const undoStateAtom = atom({
  path: ['transient', 'undoState', ':appUid', ':pageId'],
  defaultValue: false,
});

export const updatedStateAtom = atom<
  { userName: string; ts: string; displayName: string } | undefined
>({
  path: ['transient', 'updatedState', ':appUid', ':pageId'],
  defaultValue: undefined,
});

export const PageUpdateEventID = ulid();

export default function PageUpdateListener({
  appUid: currentAppUid,
  pageId: currentPageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement | null {
  const platform = usePlatform();
  const user = useUser();
  const autoPageRefresh = useStateXAtomValue(autoPageRefreshAtom, {
    params: { userName: user.userName },
  });

  useSubscription(
    'cloudio',
    (event) => {
      const { pageId, appUid, id, userName, ts, displayName } =
        event.data as PageUpdateEvent;
      if (id === PageUpdateEventID) return;
      if (platform.resource().hasPageLoaded(appUid, pageId)) {
        if (platform.getter(undoStateAtom, { params: { appUid, pageId } })) {
          // page has pending changes
          platform.setter(
            updatedStateAtom,
            { userName, ts, displayName },
            {
              params: { appUid, pageId },
            },
          );
          platform.showError(
            `Page got updated by ${displayName} just now! You will loose all your changes!`,
          );
        } else if (
          autoPageRefresh &&
          currentAppUid === appUid &&
          currentPageId === pageId
        ) {
          const pageResource = platform
            .resource()
            .refreshPage(appUid, pageId, false);
          try {
            pageResource?.fetch(emptyFunction);
          } catch (e) {
            // ignore me
          }
        } else if (autoPageRefresh) {
          platform.resource().clearPage(appUid, pageId);
        }
      }
    },
    'PageUpdateListener',
    'all',
  );

  return null;
}
